import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { toast } from 'sonner';

import { apiAdminperformancesGetCollection } from '@r/api/generated/client.ts';
import { unparse } from 'papaparse';
import { downloadCSV } from '@r/utils/utils.ts';
import type {
  ApiAdminperformancesGetCollectionParams,
  PerformancePerformanceCollectionReadModel
} from '@r/api/generated/client.schemas.ts';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { format as formatDate } from 'date-fns';
import {
  usePerformancesColumns,
  usePerformancesFilterFields,
  type PerformanceCollection
} from '@admin/components/performances/performances-columns.tsx';
import { isTargetLogicWorkspace } from '@r/utils/workspace-utils.ts';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { buildApiDateRangeParams } from '@r/utils/date-range-filter.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';

type PerformanceFilters = {
  companyName: string;
  companyUniqueCode: string;
  beneficiaryFullname: string;
  amountMin: string;
  amountMax: string;
  label: string;
  logicTypeTargetReference: string | undefined;
  referenceUniqueCode: string;
  referenceQuantityMin: string;
  referenceQuantityMax: string;
  realizedAt: string;
  realizedAtAfter: string;
  realizedAtBefore: string;
  createdAt: string;
  createdAtAfter: string;
  createdAtBefore: string;
  importedByFullname: string;
  amountSortValue: 'asc' | 'desc' | undefined;
  referenceUniqueCodeSortValue: 'asc' | 'desc' | undefined;
  referenceQuantitySortValue: 'asc' | 'desc' | undefined;
  pointAmountSortValue: 'asc' | 'desc' | undefined;
  createdAtSortValue: 'asc' | 'desc' | undefined;
  realizedAtSortValue: 'asc' | 'desc' | undefined;
};

type PerformancesQueryResult = {
  data: PerformanceCollection[];
  pagination?: { last?: number; totalItems?: number };
};

export const Performances = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isExporting, setIsExporting] = useState(false);

  const authUser = useUser();
  const user = authUser.data;
  const { workspace } = UseWorkspace(user);
  const { sanitizedWorkspaceName } = useWorkspaceName();

  async function performancesListFiltersQuery(
    _filters: PerformanceFilters,
    page: number | null = null,
    limit: number | null = null
  ): Promise<PerformancesQueryResult> {
    try {
      const {
        companyName,
        companyUniqueCode,
        beneficiaryFullname,
        amountMin,
        amountMax,
        label,
        logicTypeTargetReference,
        referenceUniqueCode,
        referenceQuantityMin,
        referenceQuantityMax,
        realizedAt,
        realizedAtAfter,
        realizedAtBefore,
        createdAt,
        createdAtAfter,
        createdAtBefore,
        importedByFullname,
        amountSortValue,
        referenceUniqueCodeSortValue,
        referenceQuantitySortValue,
        pointAmountSortValue,
        createdAtSortValue,
        realizedAtSortValue
      } = _filters;

      const queriesPayload: ApiAdminperformancesGetCollectionParams = {
        page: page ?? pageIndex + 1,
        perPage: limit ?? pageSize,
        ...(companyName ? { 'realizedByCompany.name': companyName } : {}),
        ...(companyUniqueCode ? { 'realizedByCompany.uniqueCode': companyUniqueCode } : {}),
        ...(beneficiaryFullname ? { 'realizedBy.user.fullname': beneficiaryFullname } : {}),
        ...(amountMin ? { 'amount[gte]': amountMin } : {}),
        ...(amountMax ? { 'amount[lte]': amountMax } : {}),
        ...(label ? { label } : {}),
        ...(logicTypeTargetReference
          ? { 'logicTypeTarget.reference': logicTypeTargetReference }
          : {}),
        ...(referenceUniqueCode ? { referenceUniqueCode } : {}),
        ...(referenceQuantityMin ? { 'referenceQuantity[gte]': referenceQuantityMin } : {}),
        ...(referenceQuantityMax ? { 'referenceQuantity[lte]': referenceQuantityMax } : {}),
        ...(realizedAtAfter ? { 'realizedAt[after]': realizedAtAfter } : {}),
        ...(realizedAtBefore ? { 'realizedAt[before]': realizedAtBefore } : {}),
        ...(!realizedAtAfter && !realizedAtBefore
          ? buildApiDateRangeParams('realizedAt', realizedAt)
          : {}),
        ...(createdAtAfter ? { 'createdAt[after]': createdAtAfter } : {}),
        ...(createdAtBefore ? { 'createdAt[before]': createdAtBefore } : {}),
        ...(!createdAtAfter && !createdAtBefore
          ? buildApiDateRangeParams('createdAt', createdAt)
          : {}),
        ...(importedByFullname ? { 'addedByUserWorkspace.user.fullname': importedByFullname } : {}),
        ...(amountSortValue ? { 'order[amount]': amountSortValue } : {}),
        ...(referenceUniqueCodeSortValue
          ? { 'order[referenceUniqueCode]': referenceUniqueCodeSortValue }
          : {}),
        ...(referenceQuantitySortValue
          ? { 'order[referenceQuantity]': referenceQuantitySortValue }
          : {}),
        ...(pointAmountSortValue ? { 'order[point.amount]': pointAmountSortValue } : {}),
        ...(createdAtSortValue ? { 'order[createdAt]': createdAtSortValue } : {}),
        ...(realizedAtSortValue ? { 'order[realizedAt]': realizedAtSortValue } : {}),
        ...(realizedAtSortValue ? { 'order[id]': realizedAtSortValue } : {})
      };

      const response = await apiAdminperformancesGetCollection(queriesPayload);

      type ApiPerformance = PerformancePerformanceCollectionReadModel;
      const mappedData: PerformanceCollection[] =
        (response?.data as ApiPerformance[] | undefined)?.map((p) => ({
          id: p.id,
          amount: p.amount ?? null,
          label: (p as unknown as { label?: string | null })?.label ?? null,
          realizedAt: p.realizedAt ?? null,
          createdAt: p.createdAt ?? null,
          realizedBy: {
            fullname: p.realizedBy?.user?.fullname ?? null,
            isDeleted: p.realizedBy?.user?.isDeleted
          },
          realizedByCompany: {
            name: p.realizedByCompany?.name,
            uniqueCode: p.realizedByCompany?.uniqueCode
          },
          logicTypeTarget: {
            reference: p.logicTypeTarget?.reference ?? null
          },
          referenceUniqueCode: p.referenceUniqueCode ?? null,
          referenceQuantity: p.referenceQuantity ?? null,
          point: p.point ? { amount: p.point.amount ?? null } : null,
          addedByUserWorkspace: p.addedByUserWorkspace
            ? {
                user: {
                  fullname: p.addedByUserWorkspace.user?.fullname ?? null,
                  isDeleted: p.addedByUserWorkspace.user?.isDeleted
                }
              }
            : null
        })) ?? [];

      return {
        data: mappedData,
        pagination: response?.pagination
      };
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/admin/performances')) {
        showOrvalErrorToast(error, t('toast.error.label'));
      } else {
        toast.error(`${t('toast.error.label')}`);
      }
      throw error;
    }
  }

  function performancesListFilters(): PerformanceFilters {
    const companyName =
      (columnFilters.find((filter) => filter.id === 'companyName')?.value as string) ?? '';
    const companyUniqueCode =
      (columnFilters.find((filter) => filter.id === 'companyUniqueCode')?.value as string) ?? '';
    const beneficiaryFullname =
      (columnFilters.find((filter) => filter.id === 'beneficiaryFullname')?.value as string) ?? '';
    const amountMin =
      (columnFilters.find((filter) => filter.id === 'amountMin')?.value as string) ?? '';
    const amountMax =
      (columnFilters.find((filter) => filter.id === 'amountMax')?.value as string) ?? '';
    const label = (columnFilters.find((filter) => filter.id === 'label')?.value as string) ?? '';
    const logicTypeTargetReference =
      (columnFilters.find((filter) => filter.id === 'logicTypeTargetReference')?.value as string) ??
      '';
    const referenceUniqueCode =
      (columnFilters.find((filter) => filter.id === 'referenceUniqueCode')?.value as string) ?? '';
    const referenceQuantityMin =
      (columnFilters.find((filter) => filter.id === 'referenceQuantityMin')?.value as string) ?? '';
    const referenceQuantityMax =
      (columnFilters.find((filter) => filter.id === 'referenceQuantityMax')?.value as string) ?? '';

    const realizedAt =
      (columnFilters.find((filter) => filter.id === 'realizedAt')?.value as string) ?? '';
    const realizedAtAfter =
      (columnFilters.find((filter) => filter.id === 'realizedAtAfter')?.value as string) ?? '';
    const realizedAtBefore =
      (columnFilters.find((filter) => filter.id === 'realizedAtBefore')?.value as string) ?? '';

    const createdAt =
      (columnFilters.find((filter) => filter.id === 'createdAt')?.value as string) ?? '';
    const createdAtAfter =
      (columnFilters.find((filter) => filter.id === 'createdAtAfter')?.value as string) ?? '';
    const createdAtBefore =
      (columnFilters.find((filter) => filter.id === 'createdAtBefore')?.value as string) ?? '';

    const importedByFullname =
      (columnFilters.find((filter) => filter.id === 'importedByFullname')?.value as string) ?? '';

    const sortValue = (id: string): 'asc' | 'desc' | undefined => {
      const sort = sorting.find((s) => s.id === id);
      return sort ? (sort.desc ? 'desc' : 'asc') : undefined;
    };

    const amountSortValue = sortValue('amount');
    const referenceUniqueCodeSortValue = sortValue('referenceUniqueCode');
    const referenceQuantitySortValue = sortValue('referenceQuantity');
    const pointAmountSortValue = sortValue('point.amount');
    const createdAtSortValue = sortValue('createdAt');
    let realizedAtSortValue = sortValue('realizedAt');

    if (
      !amountSortValue &&
      !referenceUniqueCodeSortValue &&
      !referenceQuantitySortValue &&
      !pointAmountSortValue &&
      !createdAtSortValue &&
      !realizedAtSortValue
    ) {
      realizedAtSortValue = 'desc';
    }

    return {
      companyName,
      companyUniqueCode,
      beneficiaryFullname,
      amountMin,
      amountMax,
      label,
      logicTypeTargetReference,
      referenceUniqueCode,
      referenceQuantityMin,
      referenceQuantityMax,
      realizedAt,
      realizedAtAfter,
      realizedAtBefore,
      createdAt,
      createdAtAfter,
      createdAtBefore,
      importedByFullname,
      amountSortValue,
      referenceUniqueCodeSortValue,
      referenceQuantitySortValue,
      pointAmountSortValue,
      createdAtSortValue,
      realizedAtSortValue
    };
  }

  function getPerformanceDataForExport(performance: PerformanceCollection) {
    const sanitize = (value: string | number | undefined | null) =>
      String(value ?? '').replace(/[\r\n\t]/g, '');
    const formatForExportDate = (value?: string | null) =>
      value ? formatDate(new Date(value), 'yyyy-MM-dd') : '';

    const base = [
      sanitize(performance.realizedByCompany?.name),
      sanitize(performance.realizedByCompany?.uniqueCode),
      sanitize(performance.realizedBy?.fullname),
      sanitize(`${performance.amount} €`),
      sanitize(performance.label),
      sanitize(performance.referenceUniqueCode),
      sanitize(performance.referenceQuantity),
      sanitize(performance.point?.amount),
      sanitize(formatForExportDate(performance.realizedAt)),
      sanitize(formatForExportDate(performance.createdAt)),
      sanitize(performance.addedByUserWorkspace?.user?.fullname)
    ];

    if (isTargetLogicWorkspace(workspace)) {
      base.splice(5, 0, sanitize(performance.logicTypeTarget?.reference));
    }

    return base;
  }

  const performancesListQuery = useQuery<PerformancesQueryResult>({
    queryKey: ['performances_list'],
    queryFn: async (): Promise<PerformancesQueryResult> => {
      return await performancesListFiltersQuery(performancesListFilters());
    }
  });

  const filterFields = usePerformancesFilterFields();

  const performancesTablePersistenceKey = useMemo(
    // Cle de persistance isolee par utilisateur pour conserver ses preferences.
    () => getAdminTablePersistenceKey('performances', authUser.data?.id),
    [authUser.data?.id]
  );

  const { table } = useDataTable<PerformanceCollection, unknown>({
    data: performancesListQuery.data?.data ?? [],
    columns: usePerformancesColumns(),
    filterFields,
    pageCount: performancesListQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: true,
    persistence: {
      // Persiste pagination, tri et filtres en localStorage.
      key: performancesTablePersistenceKey
    }
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;
  const sorting = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });

  async function exportData() {
    setIsExporting(true);

    let allData: PerformanceCollection[] = [];
    const limit = 500;

    const totalPageCount = Math.ceil(
      (performancesListQuery.data?.pagination?.totalItems ?? 1) / limit
    );
    const promises = Array.from({ length: totalPageCount }, (_, i) =>
      performancesListFiltersQuery(performancesListFilters(), i + 1, limit)
    );

    const pagesData = await Promise.all(promises);
    allData = pagesData.flatMap((pageData) => pageData.data);

    const fields = [
      t('label.performance.company.name'),
      t('label.performance.unique.code'),
      t('label.performances.beneficiary'),
      t('label.performances.amount'),
      t('label.performance.pattern'),
      t('label.performances.reference.unique.code'),
      t('label.performances.reference.quantity'),
      t('label.point.target.amount'),
      t('label.performances.realization.date'),
      t('label.performances.import.date'),
      t('label.performances.import.user.fullname')
    ];

    if (isTargetLogicWorkspace(workspace)) {
      fields.splice(5, 0, t('label.performances.target.reference'));
    }

    const csv = unparse({
      fields: fields,
      data: allData.map((p) => getPerformanceDataForExport(p))
    });

    const currentDate = new Date();
    const baseFileName = `performances-export-${currentDate.toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    const fileName = sanitizedWorkspaceName
      ? `${sanitizedWorkspaceName}_${baseFileName}`
      : baseFileName;
    downloadCSV(csv, fileName);

    setIsExporting(false);
  }

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    if (
      pageIndex != prevValues.pageIndex ||
      pageSize != prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) != JSON.stringify(prevValues.sorting)
    ) {
      performancesListQuery.refetch();
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting]);

  return (
    <>
      <span className="text-4xl font-bold">{t('admin.performances.title')}</span>

      {performancesListQuery.isLoading && (
        <DataTableSkeleton columnCount={9} cellWidths={['24rem', '12rem', '12rem', '8rem']} />
      )}

      {performancesListQuery.isSuccess && (
        <>
          <DataTableAdvancedToolbar
            table={table}
            filterFields={filterFields}
            onExport={exportData}
            isExporting={isExporting}
          />
          <DataTable
            table={table}
            totalItems={performancesListQuery.data?.pagination?.totalItems}
          />
        </>
      )}
    </>
  );
};
